import React, { useState } from 'react';
import axios from 'axios';
import { Trash2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PurgeOrdersCard = () => {
  const [beforeDate, setBeforeDate] = useState('2026-10-15');
  const [preview, setPreview] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const call = async (dryRun) => {
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/maintenance/purge-orders`, {
        before_date: beforeDate, dry_run: dryRun, admin_password: password, confirm_text: confirmText,
      });
      if (dryRun) { setPreview(res.data); setResult(null); }
      else { setResult(res.data); setPreview(null); setPassword(''); setConfirmText(''); }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const canDelete = preview && preview.orders > 0 && password && confirmText.trim().toUpperCase() === 'DELETE';

  return (
    <div data-testid="purge-orders-card" className="card-brutalist bg-white p-4 sm:p-6 border-red-600">
      <h3 className="text-base font-black font-mono uppercase flex items-center gap-2 text-red-700"><Trash2 size={16} />Delete Old Orders</h3>
      <p className="text-xs font-mono text-gray-600 mt-1 mb-4">
        Permanently removes every order created before the chosen date (India time), with its refund records, invoices and payment logs. This cannot be undone. Money already paid or refunded in Razorpay is not affected.
      </p>
      <div className="flex gap-2 flex-wrap items-end mb-3">
        <label className="text-xs font-mono uppercase">
          <span className="block mb-1">Delete orders created before</span>
          <input data-testid="purge-before-date-input" type="date" value={beforeDate}
            onChange={(e) => { setBeforeDate(e.target.value); setPreview(null); }}
            className="px-3 py-2 border-2 border-black font-mono text-sm" />
        </label>
        <button data-testid="purge-preview-btn" onClick={() => call(true)} disabled={busy || !beforeDate}
          className="px-4 py-2 border-2 border-black bg-white font-mono uppercase text-xs font-bold hover:bg-gray-100 disabled:opacity-50">
          {busy && !preview ? 'Checking…' : 'Check what will be deleted'}
        </button>
      </div>

      {preview && (
        <div data-testid="purge-preview" className="p-3 border-2 border-red-600 bg-red-50 font-mono text-xs space-y-3">
          <div className="flex items-start gap-2 text-red-700 font-bold">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span data-testid="purge-preview-summary">
              {preview.orders} orders ({preview.paid_orders} paid), {preview.refund_records} refund records, {preview.invoices} invoices, {preview.payment_transactions} payment logs will be deleted.
            </span>
          </div>
          {preview.orders > 0 && (
            <div className="flex gap-2 flex-wrap">
              <input data-testid="purge-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password" className="px-3 py-2 border-2 border-black text-sm flex-1 min-w-[160px]" />
              <input data-testid="purge-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE" className="px-3 py-2 border-2 border-black text-sm flex-1 min-w-[140px]" />
              <button data-testid="purge-confirm-btn" onClick={() => call(false)} disabled={busy || !canDelete}
                className="px-4 py-2 border-2 border-black bg-red-600 text-white uppercase font-bold disabled:opacity-40">
                {busy ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          )}
        </div>
      )}

      {error && <p data-testid="purge-error" className="text-red-600 text-sm font-mono mt-3">{error}</p>}
      {result && (
        <p data-testid="purge-result" className="mt-3 p-3 border-2 border-black bg-green-50 font-mono text-xs">
          Deleted {result.deleted.orders} orders, {result.refund_records} refund records, {result.deleted.invoices} invoices and {result.deleted.payment_transactions} payment logs. Refresh the page to update the lists.
        </p>
      )}
    </div>
  );
};
